/**
 * Ergonomic wrapper components over the GSAP hooks. Prefer these in JSX; drop to
 * the hooks when you need to attach motion to an existing element/ref.
 *
 * GSAP is heavy — this barrel is intentionally NOT re-exported from `ui/index` so
 * pages that don't animate never pull it into their bundle.
 */
import type { CSSProperties, ElementType, ReactNode } from 'react';
import { cn } from '../cn';
import {
  useGsapReveal,
  useGsapStagger,
  useGsapCounter,
  useGsapParallax,
  useGsapDraw,
} from './hooks';

/** Fade + lift children into view once. Same API as the old framer-motion Reveal. */
export function Reveal({
  children,
  delay = 0,
  y = 24,
  className,
  as: As = 'div',
}: {
  children: ReactNode;
  delay?: number;
  y?: number;
  className?: string;
  as?: ElementType;
}) {
  const ref = useGsapReveal<HTMLDivElement>({ delay, y });
  return (
    <As ref={ref} className={className}>
      {children}
    </As>
  );
}

/** Stagger direct children (or a selector) into view once. */
export function Stagger({
  children,
  className,
  stagger,
  selector,
  y,
}: {
  children: ReactNode;
  className?: string;
  stagger?: number;
  selector?: string;
  y?: number;
}) {
  const ref = useGsapStagger<HTMLDivElement>({ stagger, selector, y });
  return (
    <div ref={ref} className={className}>
      {children}
    </div>
  );
}

/** A number that counts up from 0 when scrolled into view. */
export function CountUp({
  value,
  format,
  duration,
  className,
  prefix,
  suffix,
}: {
  value: number;
  format?: (n: number) => string;
  duration?: number;
  className?: string;
  prefix?: ReactNode;
  suffix?: ReactNode;
}) {
  const ref = useGsapCounter(value, { format, duration });
  return (
    <span className={className}>
      {prefix}
      <span ref={ref} />
      {suffix}
    </span>
  );
}

/** Scroll-linked parallax drift. Public-site decorative art only. */
export function ParallaxLayer({
  children,
  speed,
  axis,
  clamp,
  className,
  style,
}: {
  children: ReactNode;
  speed?: number;
  axis?: 'y' | 'x';
  clamp?: number;
  className?: string;
  style?: CSSProperties;
}) {
  const ref = useGsapParallax<HTMLDivElement>({ speed, axis, clamp });
  return (
    <div ref={ref} className={cn('will-change-transform', className)} style={style}>
      {children}
    </div>
  );
}

/**
 * Wraps inline SVG line-art so its strokes draw themselves on scroll-in.
 * Pass the raw `<path>`/`<polyline>` children; this renders the `<svg>` shell.
 */
export function DrawArt({
  children,
  viewBox = '0 0 24 24',
  className,
  duration,
  stagger,
  ...rest
}: {
  children: ReactNode;
  viewBox?: string;
  className?: string;
  duration?: number;
  stagger?: number;
} & React.SVGProps<SVGSVGElement>) {
  const ref = useGsapDraw<SVGSVGElement>({ duration, stagger });
  return (
    <svg ref={ref} viewBox={viewBox} className={className} aria-hidden="true" {...rest}>
      {children}
    </svg>
  );
}
